import { useState, useCallback, useRef, useEffect } from 'react';
import './marqueeSelection.css'; // Import CSS for selection prevention

type Rect = {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
};

type FieldLike = { position?: Rect } & Record<string, any>;

type MarqueeSelection = {
  start: { x: number; y: number } | null;
  current: { x: number; y: number } | null;
  isActive: boolean;
};

type UseMarqueeSelectionProps<T extends FieldLike> = {
  containerRef: React.RefObject<HTMLDivElement>;
  imgSize: { width: number; height: number };
  fields: T[];
  onSelectionChange: (selectedIndices: number[]) => void;
  multiSelectKey?: 'shiftKey' | 'ctrlKey' | 'metaKey';
  disabled?: boolean;
  zoom?: number; // visual scale factor (1 = 100%)
};

const fieldInsideRect = (field: FieldLike, rect: Rect): boolean => {
  // Use position object if available, otherwise use direct properties
  const fieldX0 = field.position?.x0 ?? field.x0;
  const fieldY0 = field.position?.y0 ?? field.y0;
  const fieldX1 = field.position?.x1 ?? field.x1;
  const fieldY1 = field.position?.y1 ?? field.y1;

  // Check for INTERSECTION, not containment
  return !(
    fieldX1 < rect.x0 ||
    fieldX0 > rect.x1 ||
    fieldY1 < rect.y0 ||
    fieldY0 > rect.y1
  );
};

export function useMarqueeSelection<T extends FieldLike>({
  containerRef,
  imgSize,
  fields,
  onSelectionChange,
  multiSelectKey = 'shiftKey',
  disabled = false,
  zoom = 1,
}: UseMarqueeSelectionProps<T>) {
  const [marquee, setMarquee] = useState<MarqueeSelection>({
    start: null,
    current: null,
    isActive: false,
  });
  
  // Use a ref to track the latest marquee state
  const marqueeRef = useRef(marquee);
  useEffect(() => {
    marqueeRef.current = marquee;
  }, [marquee]);
  
  const startPointRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  
  const getCorrectedPoint = useCallback((e: MouseEvent | React.MouseEvent) => {
    if (!containerRef.current) return { x: 0, y: 0 };

    // In the form editor, zoom is applied via CSS transform: scale(zoom) with
    // transform-origin: top left. This means the correct inverse transform is
    // simply dividing the mouse position (measured in the scaled DOM) by zoom.
    const rect = containerRef.current.getBoundingClientRect();
    const z = zoom || 1;
    return {
      x: (e.clientX - rect.left) / z,
      y: (e.clientY - rect.top) / z,
    };
  }, [containerRef, zoom]);
  
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (disabled) return;
    if (e.button !== 0) return; // Only left mouse button
    if (e[multiSelectKey]) return; // Skip if multi-select key is pressed

    e.preventDefault(); // Prevent text selection during drag
    e.stopPropagation(); // Stop event propagation to parent elements

    const container = containerRef.current;
    if (!container) return;

    const startPoint = getCorrectedPoint(e as unknown as MouseEvent);
    startPointRef.current = startPoint;  // Store PDF coordinates

    marqueeRef.current = {
      start: startPoint,  // Use PDF coordinates
      current: startPoint,
      isActive: true,
    };
    setMarquee({
      start: startPoint,
      current: startPoint,
      isActive: true,
    });

    // Add CSS class to disable text selection
    if (containerRef.current) {
      containerRef.current.classList.add('no-user-select');
    }

    // Let effect below attach proper document listeners while active
  }, [multiSelectKey, disabled, getCorrectedPoint]);

  useEffect(() => {
    if (disabled) return;
    if (!containerRef.current) return;

    const container = containerRef.current;
    container.addEventListener('mousedown', handleMouseDown as any);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown as any);
    };
  }, [handleMouseDown, disabled]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (disabled) return;
    e.preventDefault(); // Prevent text selection during drag
    e.stopPropagation(); // Stop event propagation

    if (!marqueeRef.current.isActive || !containerRef.current) return;
    
    const currentPoint = getCorrectedPoint(e);
    setMarquee(prev => ({
      ...prev,
      current: currentPoint,
    }));
    
    // Calculate marquee bounds
    const startPoint = startPointRef.current;
    const marqueeRect = {
      x0: Math.min(startPoint.x, currentPoint.x),
      y0: Math.min(startPoint.y, currentPoint.y),
      x1: Math.max(startPoint.x, currentPoint.x),
      y1: Math.max(startPoint.y, currentPoint.y)
    };
    
    // Add minimum selection threshold
    const MIN_SELECTION_SIZE = 5; // Minimum 5px in PDF coordinates
    
    // Skip selection if too small
    if (
      (marqueeRect.x1 - marqueeRect.x0) < MIN_SELECTION_SIZE ||
      (marqueeRect.y1 - marqueeRect.y0) < MIN_SELECTION_SIZE
    ) {
      return;
    }
    
    const selectedIndices: number[] = [];
    fields.forEach((field, index) => {
      // Use field coordinates directly (PDF coordinates)
      const fieldRect = {
        x0: field.position?.x0 ?? field.x0,
        y0: field.position?.y0 ?? field.y0,
        x1: field.position?.x1 ?? field.x1,
        y1: field.position?.y1 ?? field.y1,
      };

      // Check for intersection with marquee (both in PDF coordinates)
      const inside = fieldInsideRect(fieldRect, marqueeRect);
      
      if (inside) {
        selectedIndices.push(index);
      }
    });
    
    onSelectionChange(selectedIndices);
  }, [fields, getCorrectedPoint, onSelectionChange, disabled]);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (disabled) return;
    e.preventDefault(); // Prevent text selection on release
    e.stopPropagation(); // Stop event propagation

    if (marquee.isActive) {
      // Final selection update on mouseup to avoid missing the last movement
      if (containerRef.current) {
        const currentPoint = getCorrectedPoint(e);
        const startPoint = startPointRef.current;
        const marqueeRect = {
          x0: Math.min(startPoint.x, currentPoint.x),
          y0: Math.min(startPoint.y, currentPoint.y),
          x1: Math.max(startPoint.x, currentPoint.x),
          y1: Math.max(startPoint.y, currentPoint.y)
        };
        const MIN_SELECTION_SIZE = 5;
        if (
          (marqueeRect.x1 - marqueeRect.x0) >= MIN_SELECTION_SIZE &&
          (marqueeRect.y1 - marqueeRect.y0) >= MIN_SELECTION_SIZE
        ) {
          const selectedIndices: number[] = [];
          fields.forEach((field, index) => {
            const fieldRect = {
              x0: field.position?.x0 ?? (field as any).x0,
              y0: field.position?.y0 ?? (field as any).y0,
              x1: field.position?.x1 ?? (field as any).x1,
              y1: field.position?.y1 ?? (field as any).y1,
            };
            if (fieldInsideRect(fieldRect, marqueeRect)) selectedIndices.push(index);
          });
          onSelectionChange(selectedIndices);
        }
      }
      setMarquee(prev => ({
        ...prev,
        isActive: false,
      }));
      // Re-enable text selection on container
      if (containerRef.current) {
        containerRef.current.classList.remove('no-user-select');
      }
    }
  }, [marquee.isActive, disabled, fields, getCorrectedPoint, onSelectionChange]);

  // Add event listeners
  useEffect(() => {
    if (disabled) return;
    if (marquee.isActive) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [marquee.isActive, handleMouseMove, handleMouseUp, disabled]);

  // Calculate marquee rectangle for rendering
  const marqueeRect = marquee.start && marquee.current ? {
    left: Math.min(marquee.start.x, marquee.current.x),
    right: Math.max(marquee.start.x, marquee.current.x),
    top: Math.min(marquee.start.y, marquee.current.y),
    bottom: Math.max(marquee.start.y, marquee.current.y),
  } : null;

  return {
    marqueeRect,
    isSelecting: marquee.isActive,
    onMouseDown: handleMouseDown,
  };
}

export default useMarqueeSelection;
